import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { motion } from 'motion/react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ContactPhone as ContactsIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

const accentColor = '#4698D3';

const TruncatedError = ({ message }) => {
  const [expanded, setExpanded] = React.useState(false);
  const MAX_LEN = 80;
  if (!message || message.length <= MAX_LEN) return <span>{message}</span>;
  return (
    <span>
      {expanded ? message : `${message.slice(0, MAX_LEN)}...`}
      <span
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        style={{ color: '#4698D3', cursor: 'pointer', marginLeft: 4, fontWeight: 600, textDecoration: 'underline' }}
      >
        {expanded ? 'Show less' : 'Show more'}
      </span>
    </span>
  );
};

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Metropolis, sans-serif',
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: '#E5E5E5' },
    '&:hover fieldset': { borderColor: accentColor },
    '&.Mui-focused fieldset': { borderColor: accentColor },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Metropolis, sans-serif',
    color: '#666666',
    '&.Mui-focused': { color: accentColor },
  },
};

const SectionHeader = ({ title, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
    <Typography variant="h6" sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66', fontSize: '1.1rem' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const AlternateContactSection = ({ title, type, data, setData, saving, deleting, onSave, onDelete, readOnly }) => (
  <motion.div variants={sectionVariants}>
    <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '8px', mb: 3 }}>
      <SectionHeader title={title}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            disabled={readOnly || deleting || (!data.name && !data.email_address)}
            onClick={() => onDelete(type)}
            sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', borderRadius: '8px' }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
            disabled={readOnly || saving}
            onClick={() => onSave(type, data)}
            sx={{
              fontFamily: 'Metropolis, sans-serif',
              textTransform: 'none',
              borderRadius: '8px',
              backgroundColor: accentColor,
              '&:hover': { backgroundColor: '#3580B8' },
            }}
          >
            Save
          </Button>
        </Box>
      </SectionHeader>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField label="Name" size="small" value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} sx={inputStyle} />
        <TextField label="Title" size="small" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} sx={inputStyle} />
        <TextField label="Email Address" size="small" value={data.email_address} onChange={(e) => setData({ ...data, email_address: e.target.value })} sx={inputStyle} />
        <TextField label="Phone Number" size="small" value={data.phone_number} onChange={(e) => setData({ ...data, phone_number: e.target.value })} sx={inputStyle} />
      </Box>
    </Paper>
  </motion.div>
);

const AccountContactDetail = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const isReadOnly = useReadOnlyMode();
  const [loading, setLoading] = useState(true);
  const [savingPrimary, setSavingPrimary] = useState(false);
  const [savingAlternate, setSavingAlternate] = useState({});
  const [deletingAlternate, setDeletingAlternate] = useState({});
  const [startingEmail, setStartingEmail] = useState(false);
  const [acceptingEmail, setAcceptingEmail] = useState(false);

  // Contact state
  const [primaryContact, setPrimaryContact] = useState({
    full_name: '', address_line_1: '', address_line_2: '', address_line_3: '',
    city: '', state_or_region: '', postal_code: '',
    country_code: '', phone_number: '', company_name: '', website_url: '',
  });
  const [billing, setBilling] = useState({ name: '', title: '', email_address: '', phone_number: '' });
  const [operations, setOperations] = useState({ name: '', title: '', email_address: '', phone_number: '' });
  const [security, setSecurity] = useState({ name: '', title: '', email_address: '', phone_number: '' });
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [primaryEmailUnsupported, setPrimaryEmailUnsupported] = useState(false);
  const [primaryEmailUnsupportedReason, setPrimaryEmailUnsupportedReason] = useState('');

  // Email update state
  const [newEmail, setNewEmail] = useState('');
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');

  // Delete confirm dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '' });

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccountContacts(accountId);
      const data = response.success ? response.data : response;
      if (data) {
        if (data.primary) setPrimaryContact(data.primary);
        if (data.billing) setBilling(data.billing);
        if (data.operations) setOperations(data.operations);
        if (data.security) setSecurity(data.security);
        if (data.primary_email) setPrimaryEmail(data.primary_email);
        setPrimaryEmailUnsupported(!!data.primary_email_unsupported);
        setPrimaryEmailUnsupportedReason(data.primary_email_unsupported_reason || '');
      }
    } catch (error) {
      toast.error(<TruncatedError message={error.message || 'Failed to load contact information'} />);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleSavePrimary = async () => {
    setSavingPrimary(true);
    try {
      await apiService.updatePrimaryContact(accountId, primaryContact);
      toast.success('Primary contact updated');
    } catch (error) {
      toast.error(<TruncatedError message={error.message || 'Failed to update primary contact'} />);
    } finally {
      setSavingPrimary(false);
    }
  };

  const handleSaveAlternate = async (type, data) => {
    setSavingAlternate(prev => ({ ...prev, [type]: true }));
    try {
      await apiService.updateAlternateContact(accountId, type, data);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} contact updated`);
    } catch (error) {
      toast.error(<TruncatedError message={error.message || `Failed to update ${type} contact`} />);
    } finally {
      setSavingAlternate(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteAlternate = async (type) => {
    setDeleteDialog({ open: false, type: '' });
    setDeletingAlternate(prev => ({ ...prev, [type]: true }));
    try {
      await apiService.deleteAlternateContact(accountId, type);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} contact deleted`);
      const emptyContact = { name: '', title: '', email_address: '', phone_number: '' };
      if (type === 'billing') setBilling(emptyContact);
      if (type === 'operations') setOperations(emptyContact);
      if (type === 'security') setSecurity(emptyContact);
    } catch (error) {
      toast.error(<TruncatedError message={error.message || `Failed to delete ${type} contact`} />);
    } finally {
      setDeletingAlternate(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleStartEmailUpdate = async () => {
    if (!newEmail) {
      toast.error('Please enter a new email address');
      return;
    }
    setStartingEmail(true);
    try {
      await apiService.startPrimaryEmailUpdate(accountId, newEmail);
      toast.success('OTP sent to the new email address');
      setPendingEmail(newEmail);
      setOtpDialogOpen(true);
    } catch (error) {
      toast.error(<TruncatedError message={error.message || 'Failed to start email update'} />);
    } finally {
      setStartingEmail(false);
    }
  };

  const handleAcceptEmailUpdate = async () => {
    if (!otp) {
      toast.error('Please enter the OTP');
      return;
    }
    setAcceptingEmail(true);
    try {
      await apiService.acceptPrimaryEmailUpdate(accountId, pendingEmail, otp);
      toast.success('Root email updated successfully');
      setPrimaryEmail(pendingEmail);
      setOtpDialogOpen(false);
      setOtp('');
      setNewEmail('');
      setPendingEmail('');
    } catch (error) {
      toast.error(<TruncatedError message={error.message || 'Failed to verify OTP'} />);
    } finally {
      setAcceptingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', py: 4, px: 4, backgroundColor: '#F9FBFD' }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
            py: 6,
          }}
        >
          <CircularProgress size={40} sx={{ color: accentColor }} />
          <Typography
            sx={{
              color: '#666666',
              fontFamily: 'Metropolis, sans-serif',
              fontWeight: 500,
              fontSize: '0.95rem',
            }}
          >
            Loading contact information...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
        {/* Header */}
        <motion.div variants={sectionVariants} style={{ marginBottom: 24 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              border: '1px solid #E5E5E5',
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title="Back to Accounts">
                  <IconButton
                    onClick={() => navigate('/accounts')}
                    sx={{
                      backgroundColor: '#F9FBFD',
                      border: '1px solid #E5E5E5',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: '#EBF5FB',
                        borderColor: accentColor,
                      },
                    }}
                  >
                    <BackIcon sx={{ color: '#666666' }} />
                  </IconButton>
                </Tooltip>
                <Avatar sx={{ bgcolor: accentColor, width: 48, height: 48 }}>
                  <ContactsIcon sx={{ fontSize: 24 }} />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Bitter, Georgia, serif',
                      fontWeight: 600,
                      fontSize: '1.75rem',
                      color: '#253E66',
                    }}
                  >
                    Account {accountId}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.875rem',
                      color: '#999999',
                      fontWeight: 500,
                    }}
                  >
                    Manage contact information for this AWS account
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={fetchContacts}
                sx={{
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#EBF5FB',
                    borderColor: accentColor,
                  },
                }}
              >
                <RefreshIcon sx={{ color: accentColor }} />
              </IconButton>
            </Box>
          </Paper>
        </motion.div>

        {/* Root Email Section */}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '8px', mb: 3 }}>
            <SectionHeader title="Root Email" />
            {primaryEmailUnsupported ? (
              <Alert
                severity="warning"
                sx={{ fontFamily: 'Metropolis, sans-serif', borderRadius: '8px' }}
              >
                {primaryEmailUnsupportedReason || "AWS does not expose this account's root email via API."}
              </Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <EmailIcon sx={{ color: accentColor }} />
                  <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 500, color: '#253E66' }}>
                    Current: {primaryEmail || 'Not available'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', mb: 2 }}>
                  Changing the root email requires OTP verification. An OTP will be sent to the new email address.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <TextField
                    label="New Email Address"
                    size="small"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    sx={{ ...inputStyle, flex: 1 }}
                    placeholder="new@example.com"
                  />
                  <Button
                    variant="contained"
                    startIcon={startingEmail ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <EmailIcon />}
                    disabled={isReadOnly || startingEmail || !newEmail}
                    onClick={handleStartEmailUpdate}
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      textTransform: 'none',
                      borderRadius: '8px',
                      backgroundColor: accentColor,
                      '&:hover': { backgroundColor: '#3580B8' },
                      whiteSpace: 'nowrap',
                      mt: '1px',
                    }}
                  >
                    Start Update
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </motion.div>

        {/* Primary Contact Section */}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '8px', mb: 3 }}>
            <SectionHeader title="Primary Contact">
              <Button
                variant="contained"
                size="small"
                startIcon={savingPrimary ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
                disabled={isReadOnly || savingPrimary}
                onClick={handleSavePrimary}
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  textTransform: 'none',
                  borderRadius: '8px',
                  backgroundColor: accentColor,
                  '&:hover': { backgroundColor: '#3580B8' },
                }}
              >
                Save
              </Button>
            </SectionHeader>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Full Name" size="small" value={primaryContact.full_name} onChange={(e) => setPrimaryContact({ ...primaryContact, full_name: e.target.value })} sx={inputStyle} />
              <TextField label="Company Name" size="small" value={primaryContact.company_name} onChange={(e) => setPrimaryContact({ ...primaryContact, company_name: e.target.value })} sx={inputStyle} />
              <TextField label="Phone Number" size="small" value={primaryContact.phone_number} onChange={(e) => setPrimaryContact({ ...primaryContact, phone_number: e.target.value })} sx={inputStyle} />
              <TextField label="Website URL" size="small" value={primaryContact.website_url} onChange={(e) => setPrimaryContact({ ...primaryContact, website_url: e.target.value })} sx={inputStyle} />
              <TextField label="Address Line 1" size="small" value={primaryContact.address_line_1} onChange={(e) => setPrimaryContact({ ...primaryContact, address_line_1: e.target.value })} sx={{ ...inputStyle, gridColumn: { md: '1 / -1' } }} />
              <TextField label="Address Line 2" size="small" value={primaryContact.address_line_2} onChange={(e) => setPrimaryContact({ ...primaryContact, address_line_2: e.target.value })} sx={inputStyle} />
              <TextField label="Address Line 3" size="small" value={primaryContact.address_line_3} onChange={(e) => setPrimaryContact({ ...primaryContact, address_line_3: e.target.value })} sx={inputStyle} />
              <TextField label="City" size="small" value={primaryContact.city} onChange={(e) => setPrimaryContact({ ...primaryContact, city: e.target.value })} sx={inputStyle} />
              <TextField label="State / Region" size="small" value={primaryContact.state_or_region} onChange={(e) => setPrimaryContact({ ...primaryContact, state_or_region: e.target.value })} sx={inputStyle} />
              <TextField label="Postal Code" size="small" value={primaryContact.postal_code} onChange={(e) => setPrimaryContact({ ...primaryContact, postal_code: e.target.value })} sx={inputStyle} />
              <TextField label="Country Code" size="small" value={primaryContact.country_code} onChange={(e) => setPrimaryContact({ ...primaryContact, country_code: e.target.value })} sx={inputStyle} placeholder="e.g. US, IN" />
            </Box>
          </Paper>
        </motion.div>

        {/* Alternate Contact Sections */}
        <AlternateContactSection
          title="Billing Contact"
          type="billing"
          data={billing}
          setData={setBilling}
          saving={savingAlternate.billing}
          deleting={deletingAlternate.billing}
          onSave={handleSaveAlternate}
          onDelete={(type) => setDeleteDialog({ open: true, type })}
          readOnly={isReadOnly}
        />
        <AlternateContactSection
          title="Operations Contact"
          type="operations"
          data={operations}
          setData={setOperations}
          saving={savingAlternate.operations}
          deleting={deletingAlternate.operations}
          onSave={handleSaveAlternate}
          onDelete={(type) => setDeleteDialog({ open: true, type })}
          readOnly={isReadOnly}
        />
        <AlternateContactSection
          title="Security Contact"
          type="security"
          data={security}
          setData={setSecurity}
          saving={savingAlternate.security}
          deleting={deletingAlternate.security}
          onSave={handleSaveAlternate}
          onDelete={(type) => setDeleteDialog({ open: true, type })}
          readOnly={isReadOnly}
        />

        {/* OTP Dialog */}
        <Dialog
          open={otpDialogOpen}
          onClose={() => { setOtpDialogOpen(false); setOtp(''); }}
          PaperProps={{ sx: { borderRadius: '8px', border: '1px solid #E5E5E5', minWidth: 400 } }}
        >
          <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
            Verify OTP
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif', mb: 2 }}>
              An OTP has been sent to <strong>{pendingEmail}</strong>. Enter it below to confirm the email change.
            </Typography>
            <TextField
              label="OTP Code"
              size="small"
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={inputStyle}
              autoFocus
              placeholder="123456"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => { setOtpDialogOpen(false); setOtp(''); }}
              sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', color: '#666666' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isReadOnly || acceptingEmail || !otp}
              onClick={handleAcceptEmailUpdate}
              startIcon={acceptingEmail ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
              sx={{
                fontFamily: 'Metropolis, sans-serif',
                textTransform: 'none',
                borderRadius: '8px',
                backgroundColor: accentColor,
                '&:hover': { backgroundColor: '#3580B8' },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, type: '' })}
          PaperProps={{ sx: { borderRadius: '8px', border: '1px solid #E5E5E5' } }}
        >
          <DialogTitle sx={{ fontFamily: 'Bitter, Georgia, serif', fontWeight: 600, color: '#253E66' }}>
            Delete {deleteDialog.type} Contact
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#666666', fontFamily: 'Metropolis, sans-serif' }}>
              Are you sure you want to delete the {deleteDialog.type} alternate contact? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setDeleteDialog({ open: false, type: '' })}
              sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', color: '#666666' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={isReadOnly}
              onClick={() => handleDeleteAlternate(deleteDialog.type)}
              sx={{ fontFamily: 'Metropolis, sans-serif', textTransform: 'none', borderRadius: '8px' }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
    </motion.div>
  );
};

export default AccountContactDetail;
